// "Continue Pattern" — repeat a partially-built rotation pattern until it
// fills the whole cycle and loops back cleanly to where it began.
//
// Use case: a lawyer building a 4-day-on / 4-day-off rotation builds the
// first 8 days by hand, then clicks Continue Pattern.
//
// The pattern is measured on the cycle CIRCLE, not on a flat timeline. A
// rotation is circular — the last week hands back to the first — so a user
// may build the pattern across that seam (Jane Sun→Thu placed in Week 2,
// David Thu→Mon in Week 1; Jane's block wraps to meet David's). On a flat
// line that reads as "latest end − earliest start = the whole rotation";
// on the circle it is an 8-day chain with a 6-day gap. So: sort the blocks
// by start, find the LARGEST empty gap between consecutive blocks going
// round the circle, and the pattern is everything that isn't that gap. The
// block just after the gap is the chain start.
//
// A pattern only repeats cleanly when its length divides the cycle evenly
// (8 days fits an 8-week rotation: 56 days = 7 repeats; a 12-day 6-on/6-off
// fits 12 weeks). When it doesn't, the result carries `suggestedWeeks` — the
// smallest rotation length (up to maxWeeks, default 12) it does divide —
// and the caller retries with `targetWeeks`. That retry REBUILDS the whole
// rotation from the chain rather than padding weeks and re-measuring: a
// chain built across the seam of a 2-week cycle is only contiguous in a
// 2-week cycle, so its blocks are re-laid from the chain start in the new
// cycle and then tiled.
//
// When the pattern is a whole number of days give or take a handover time
// that doesn't match the others (Mon 9:00 am among 8:00 am handovers), the
// failure message names the odd handover — that is by far the most common
// reason an intended 4/4 comes out as "8 days 1 hour".

import {
  DAYS_OF_WEEK,
  DAY_INDEX,
  MIN_PER_HOUR,
  MIN_PER_DAY,
  MIN_PER_WEEK,
  timeToMin,
  minToHHMM,
} from './weekMath.js'

const DAY_LONG = { mon: 'Monday', tue: 'Tuesday', wed: 'Wednesday', thu: 'Thursday', fri: 'Friday', sat: 'Saturday', sun: 'Sunday' }

// "9:00 am" for a minute-of-day.
function fmtTime12(minOfDay) {
  const m = ((minOfDay % MIN_PER_DAY) + MIN_PER_DAY) % MIN_PER_DAY
  const h24 = Math.floor(m / 60)
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  return `${h12}:${String(m % 60).padStart(2, '0')} ${h24 < 12 ? 'am' : 'pm'}`
}

function mod(n, m) {
  return ((n % m) + m) % m
}

// "an 8-week rotation" but "a 6-week rotation".
export function articleFor(n) {
  return [8, 11, 18].includes(n) ? 'an' : 'a'
}

// Human label for a pattern span: "8-day", "8-day 1-hour", "18-hour".
export function formatPatternLength(mins) {
  const days = Math.floor(mins / MIN_PER_DAY)
  const hours = Math.round((mins % MIN_PER_DAY) / MIN_PER_HOUR)
  const parts = []
  if (days > 0) parts.push(`${days}-day`)
  if (hours > 0) parts.push(`${hours}-hour`)
  return parts.join(' ') || '0-hour'
}

// Flatten one event to absolute cycle minutes, mirroring rotation.js.
function flatten(event, weekIndex) {
  const sOff = event.startWeekOffset || 0
  const eOff = event.endWeekOffset || 0
  const start = (weekIndex + sOff) * MIN_PER_WEEK
    + (DAY_INDEX[event.startDay] ?? 0) * MIN_PER_DAY
    + timeToMin(event.startTime)
  let end = (weekIndex + eOff) * MIN_PER_WEEK
    + (DAY_INDEX[event.endDay] ?? 0) * MIN_PER_DAY
    + timeToMin(event.endTime)
  if (end <= start) end += MIN_PER_WEEK  // safety, matches rotation.js
  return { start, end, event }
}

// Order the blocks round the cycle circle and find the chain: the run of
// blocks that excludes the single largest empty gap.
// → { chain: [{ offset, len, event, s }] (offset from chain start; s = start on the circle), patternMins, chainStart }
function measureChain(flat, cycleMins) {
  const blocks = flat
    .map((f) => ({ s: mod(f.start, cycleMins), len: f.end - f.start, event: f.event }))
    .sort((a, b) => a.s - b.s)
  const n = blocks.length
  let largestGap = -Infinity
  let chainStartIdx = 0
  for (let i = 0; i < n; i++) {
    const cur = blocks[i]
    const next = blocks[(i + 1) % n]
    const nextStart = i + 1 < n ? next.s : next.s + cycleMins
    const gap = nextStart - (cur.s + cur.len)
    if (gap > largestGap) { largestGap = gap; chainStartIdx = (i + 1) % n }
  }
  const patternMins = cycleMins - Math.max(0, largestGap)
  const chainStart = blocks[chainStartIdx].s
  const chain = []
  for (let j = 0; j < n; j++) {
    const b = blocks[(chainStartIdx + j) % n]
    chain.push({ offset: mod(b.s - chainStart, cycleMins), len: b.len, event: b.event, s: b.s })
  }
  chain.sort((a, b) => a.offset - b.offset)
  return { chain, patternMins, chainStart }
}

// Smallest rotation length in [minWeeks, maxWeeks] that `patternMins` tiles.
function fittingWeeks(patternMins, minWeeks, maxWeeks) {
  for (let n = minWeeks; n <= maxWeeks; n++) {
    const c = n * MIN_PER_WEEK
    if (c > patternMins && c % patternMins === 0) return n
  }
  return null
}

// When the chain's handovers don't all fall at the same time of day and the
// pattern would divide a rotation if they did, say which handover is off.
function driftHint(chain, chainStart, patternMins, minWeeks, maxWeeks, names) {
  const wholeDays = Math.round(patternMins / MIN_PER_DAY)
  if (wholeDays < 1 || wholeDays * MIN_PER_DAY === patternMins) return ''
  const fixedWeeks = fittingWeeks(wholeDays * MIN_PER_DAY, minWeeks, maxWeeks)
  if (!fixedWeeks) return ''

  // Every handover instant in chain order: each block's start, then the end.
  const handovers = chain.map((b) => ({
    abs: chainStart + b.offset,
    label: `${names[b.event.partner] || `Party ${b.event.partner}`}'s block starts`,
  }))
  const last = chain[chain.length - 1]
  handovers.push({
    abs: chainStart + last.offset + last.len,
    label: `${names[last.event.partner] || `Party ${last.event.partner}`}'s block ends`,
  })
  const tally = {}
  for (const h of handovers) {
    const tod = mod(h.abs, MIN_PER_DAY)
    tally[tod] = (tally[tod] || 0) + 1
  }
  const modeTod = Number(Object.keys(tally).sort((a, b) => tally[b] - tally[a])[0])
  const odd = handovers.filter((h) => mod(h.abs, MIN_PER_DAY) !== modeTod)
  if (!odd.length) return ''

  const describe = (h) => {
    const dayIdx = Math.floor(mod(h.abs, MIN_PER_WEEK) / MIN_PER_DAY)
    return `${h.label} ${DAY_LONG[DAYS_OF_WEEK[dayIdx]]} ${fmtTime12(h.abs)}`
  }
  return ` Handovers are at ${fmtTime12(modeTod)} except: ${odd.map(describe).join('; ')}.`
    + ` If every handover is at the same time, the pattern is exactly ${wholeDays} days`
    + ` and fits ${articleFor(fixedWeeks)} ${fixedWeeks}-week rotation.`
}

// Repeat the built pattern to fill the rotation.
//
//   continuePattern(rotation, { maxWeeks = 12, targetWeeks, partyAName, partyBName })
//
// Returns one of:
//   { ok: true, weeks, addedCount, patternMins, coveredMins }
//   { ok: false, reason, patternMins?, coveredMins?, suggestedWeeks? }
//
// `targetWeeks` rebuilds the rotation at that length from the measured
// chain (the retry after a suggestion). The input rotation is not mutated;
// `weeks` is a fresh array sized to the target, each week's events sorted
// chronologically.
export function continuePattern(rotation, opts = {}) {
  const { maxWeeks = 12, targetWeeks = null, partyAName = '', partyBName = '' } = opts
  const names = { A: partyAName || 'Party A', B: partyBName || 'Party B' }
  const weeks = rotation?.weeks || []
  const cycleMins = weeks.length * MIN_PER_WEEK

  const flat = []
  weeks.forEach((week, wi) => {
    ;(week?.events || []).forEach((e) => flat.push(flatten(e, wi)))
  })

  if (flat.length === 0) {
    return { ok: false, reason: 'Add at least one timeblock first. Continue Pattern repeats what you have built so far.' }
  }

  const tooLong = flat.find((f) => f.end - f.start > cycleMins)
  if (tooLong) {
    return { ok: false, reason: 'A timeblock cannot be longer than the whole rotation.' }
  }

  // Always measure in the cycle the user built in — that is where a chain
  // across the seam is contiguous.
  const { chain, patternMins, chainStart } = measureChain(flat, cycleMins)
  // Minutes of the pattern that blocks actually cover. When this is less than
  // patternMins the pattern carries empty time that will repeat too. That is
  // legitimate for a deliberate unassigned stretch, but it is also what you
  // get when the rotation was lengthened BEFORE pressing Continue Pattern: a
  // chain built across a 2-week seam is only contiguous in a 2-week cycle,
  // so in an 8-week one the same two blocks are simply six days apart and
  // measure as a 14-day pattern. The UI surfaces coveredMins so the user can
  // see that before agreeing.
  const coveredMins = chain.reduce((n, b) => n + b.len, 0)

  if (patternMins >= cycleMins) {
    return { ok: false, patternMins, coveredMins, reason: 'Your timeblocks already span the full rotation. There is no room left to repeat the pattern.' }
  }

  const outWeeks = targetWeeks || weeks.length
  const outCycle = outWeeks * MIN_PER_WEEK
  const minWeeks = weeks.length // never suggest fewer weeks than already hold blocks

  if (outCycle % patternMins !== 0) {
    const suggestedWeeks = fittingWeeks(patternMins, minWeeks, maxWeeks)
    return {
      ok: false,
      patternMins,
      coveredMins,
      suggestedWeeks,
      reason: `Your ${formatPatternLength(patternMins)} pattern does not repeat evenly in ${articleFor(outWeeks)} ${outWeeks}-week rotation.`
        + (suggestedWeeks
          ? ` It fits ${articleFor(suggestedWeeks)} ${suggestedWeeks}-week rotation.`
          : ` It does not fit any rotation up to ${maxWeeks} weeks.`
            + driftHint(chain, chainStart, patternMins, minWeeks, maxWeeks, names)),
    }
  }

  // Lay the chain down and tile copies forward. Anchor on the EARLIEST block
  // the user placed: when a seam-built chain is re-laid into a longer cycle
  // only one of its blocks can keep its position, and keeping the earliest
  // means Week 1 stays exactly as built and the pattern continues forward
  // from there; the block that crossed the old seam moves to the new one.
  // With the same cycle length every block lands where it already was.
  const repeats = outCycle / patternMins
  const newWeeks = Array.from({ length: outWeeks }, (_, i) => ({ ...(weeks[i] || {}), events: [] }))
  const anchor = chain.reduce((a, b) => (b.s < a.s ? b : a), chain[0])
  const layoutStart = mod(anchor.s - anchor.offset, outCycle)

  for (let k = 0; k < repeats; k++) {
    for (const b of chain) {
      const ns = mod(layoutStart + b.offset + k * patternMins, outCycle)
      const ne = ns + b.len
      const wi = Math.floor(ns / MIN_PER_WEEK)
      const endWeekOffset = Math.floor(ne / MIN_PER_WEEK) - wi  // week boundaries crossed (0..N)
      newWeeks[wi].events.push({
        ...b.event,
        startDay: DAYS_OF_WEEK[Math.floor((ns % MIN_PER_WEEK) / MIN_PER_DAY)],
        startTime: minToHHMM(ns),
        endDay: DAYS_OF_WEEK[Math.floor((ne % MIN_PER_WEEK) / MIN_PER_DAY)],
        endTime: minToHHMM(ne),
        startWeekOffset: 0,
        endWeekOffset,
      })
    }
  }

  newWeeks.forEach((w, wi) => {
    w.events.sort((a, b) => flatten(a, wi).start - flatten(b, wi).start)
  })

  return { ok: true, weeks: newWeeks, addedCount: (repeats - 1) * chain.length, patternMins, coveredMins }
}
